import { useRef, useState } from "react";
import { useCreate, useDelete, useListContext, useRefresh, useUpdate } from "ra-core";
import { Check, FileText, Paperclip, Pencil, Plus, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  CERTIFICATE_TYPE_OPTIONS,
  NOMAD_STATES,
  US_STATES,
  isCertificateValid,
} from "./constants";
import type { RAFile } from "../types";

type Certificate = {
  id: number;
  company_id: number;
  certificate_number: string;
  certificate_type: string;
  valid_states: string[];
  expiry_date: string;
  reviewed: boolean;
  notes?: string;
  attachment?: RAFile | null;
};

type EditableRow = {
  certificate_number: string;
  certificate_type: string;
  valid_states: string[];
  expiry_date: string;
  reviewed: boolean;
  notes: string;
  attachment: RAFile | null;
};

const EMPTY_ROW: EditableRow = {
  certificate_number: "",
  certificate_type: "single_state",
  valid_states: [],
  expiry_date: "",
  reviewed: false,
  notes: "",
  attachment: null,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const fileToRAFile = (file: File): RAFile => ({
  rawFile: file,
  src: URL.createObjectURL(file),
  title: file.name,
  type: file.type,
});

const AttachmentLink = ({ attachment }: { attachment?: RAFile | null }) => {
  if (!attachment?.src) return <span className="text-muted-foreground text-xs">—</span>;
  return (
    <a
      href={attachment.src}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-1 text-xs text-primary underline hover:no-underline"
      title={attachment.title}
      onClick={(e) => e.stopPropagation()}
    >
      <FileText className="h-3 w-3 shrink-0" />
      <span className="max-w-[100px] truncate">{attachment.title}</span>
    </a>
  );
};

// ---------------------------------------------------------------------------
// File picker (plain input, no RA form context needed)
// ---------------------------------------------------------------------------

const FilePicker = ({
  value,
  onChange,
}: {
  value: RAFile | null;
  onChange: (f: RAFile | null) => void;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div className="flex items-center gap-2">
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          onChange(file ? fileToRAFile(file) : null);
          // reset so the same file can be re-selected if needed
          e.target.value = "";
        }}
      />
      {value ? (
        <div className="flex items-center gap-1.5">
          <Paperclip className="h-3 w-3 text-muted-foreground" />
          <span className="text-xs max-w-[140px] truncate">{value.title}</span>
          <button
            type="button"
            onClick={() => onChange(null)}
            className="text-muted-foreground hover:text-destructive"
            title="Remove attachment"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ) : (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-7 text-xs gap-1"
          onClick={() => inputRef.current?.click()}
        >
          <Paperclip className="h-3 w-3" /> Attach file
        </Button>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Multi-state picker
// ---------------------------------------------------------------------------

const MultiStateSelect = ({
  value,
  onChange,
  singleSelect = false,
}: {
  value: string[];
  onChange: (v: string[]) => void;
  singleSelect?: boolean;
}) => {
  const toggle = (code: string) => {
    if (NOMAD_STATES.includes(code as any)) return;
    if (singleSelect) {
      onChange(value.includes(code) ? [] : [code]);
    } else {
      onChange(
        value.includes(code) ? value.filter((s) => s !== code) : [...value, code],
      );
    }
  };
  return (
    <div className="flex flex-wrap gap-1 max-w-xs">
      {US_STATES.filter((s) => !NOMAD_STATES.includes(s.value as any)).map(
        (s) => (
          <button
            key={s.value}
            type="button"
            onClick={() => toggle(s.value)}
            className={`text-xs px-1.5 py-0.5 rounded border transition-colors ${
              value.includes(s.value)
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-background border-border hover:bg-muted"
            }`}
          >
            {s.value}
          </button>
        ),
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Inline edit form
// ---------------------------------------------------------------------------

const CertificateForm = ({
  values,
  set,
}: {
  values: EditableRow;
  set: (f: keyof EditableRow, v: unknown) => void;
}) => (
  <div className="flex flex-col gap-3 p-3 border rounded-lg bg-muted/30">
    <div className="grid grid-cols-2 gap-3">
      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-muted-foreground">
          Certificate Number *
        </label>
        <Input
          value={values.certificate_number}
          onChange={(e) => set("certificate_number", e.target.value)}
          placeholder="e.g. TX-12345"
          className="h-8 text-sm"
        />
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-muted-foreground">
          Type *
        </label>
        <Select
          value={values.certificate_type}
          onValueChange={(v) => {
            set("certificate_type", v);
            if (v === "single_state" && values.valid_states.length > 1) {
              set("valid_states", values.valid_states.slice(0, 1));
            }
          }}
        >
          <SelectTrigger className="h-8 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CERTIFICATE_TYPE_OPTIONS.map((o) => (
              <SelectItem key={o.id} value={o.id} className="text-sm">
                {o.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-xs font-medium text-muted-foreground">
          Expiry Date *
        </label>
        <Input
          value={values.expiry_date}
          onChange={(e) => set("expiry_date", e.target.value)}
          type="date"
          className="h-8 text-sm"
        />
      </div>
      <div className="flex items-end gap-2 pb-1">
        <Checkbox
          id="reviewed"
          checked={values.reviewed}
          onCheckedChange={(v) => set("reviewed", !!v)}
        />
        <label htmlFor="reviewed" className="text-sm cursor-pointer">
          Reviewed
        </label>
      </div>
    </div>
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-muted-foreground">
        Valid States
      </label>
      <MultiStateSelect
        value={values.valid_states}
        onChange={(v) => set("valid_states", v)}
        singleSelect={values.certificate_type === "single_state"}
      />
    </div>
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-muted-foreground">Notes</label>
      <Input
        value={values.notes}
        onChange={(e) => set("notes", e.target.value)}
        placeholder="Optional notes"
        className="h-8 text-sm"
      />
    </div>
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-muted-foreground">
        Certificate File
      </label>
      <FilePicker
        value={values.attachment}
        onChange={(f) => set("attachment", f)}
      />
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Row (read + edit)
// ---------------------------------------------------------------------------

const CertificateRow = ({
  cert,
  onDelete,
  onSave,
}: {
  cert: Certificate;
  onDelete: (id: number) => void;
  onSave: (id: number, data: Partial<Certificate>, previous: Certificate) => void;
}) => {
  const [editing, setEditing] = useState(false);
  const [values, setValues] = useState<EditableRow>({
    certificate_number: cert.certificate_number,
    certificate_type: cert.certificate_type,
    valid_states: cert.valid_states ?? [],
    expiry_date: cert.expiry_date,
    reviewed: cert.reviewed,
    notes: cert.notes ?? "",
    attachment: cert.attachment ?? null,
  });
  const set = (f: keyof EditableRow, v: unknown) =>
    setValues((prev) => ({ ...prev, [f]: v }));

  const valid = isCertificateValid(cert);
  const expired = new Date(cert.expiry_date) < new Date();

  if (editing) {
    return (
      <tr className="border-b bg-muted/20">
        <td colSpan={7} className="p-3">
          <CertificateForm values={values} set={set} />
          <div className="flex gap-2 mt-3">
            <Button
              size="sm"
              onClick={() => {
                onSave(cert.id, values, cert);
                setEditing(false);
              }}
              disabled={!values.certificate_number || !values.expiry_date}
            >
              <Check className="h-3 w-3 mr-1" /> Save
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setEditing(false)}
            >
              <X className="h-3 w-3 mr-1" /> Cancel
            </Button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-b hover:bg-muted/30 text-sm">
      <td className="p-2 font-mono">{cert.certificate_number}</td>
      <td className="p-2">
        {CERTIFICATE_TYPE_OPTIONS.find((o) => o.id === cert.certificate_type)
          ?.name ?? cert.certificate_type}
      </td>
      <td className="p-2">
        <div className="flex flex-wrap gap-1">
          {(cert.valid_states ?? []).map((s) => (
            <span
              key={s}
              className="text-xs px-1.5 py-0.5 rounded bg-muted border border-border"
            >
              {s}
            </span>
          ))}
        </div>
      </td>
      <td className="p-2 whitespace-nowrap">
        <span className={expired ? "text-destructive font-medium" : ""}>
          {cert.expiry_date}
        </span>
      </td>
      <td className="p-2">
        <Badge
          variant={valid ? "default" : "destructive"}
          className="text-xs"
        >
          {valid ? "Valid" : expired ? "Expired" : "Not Reviewed"}
        </Badge>
      </td>
      <td className="p-2">
        <AttachmentLink attachment={cert.attachment} />
      </td>
      <td className="p-2">
        <div className="flex gap-1">
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7"
            onClick={() => setEditing(true)}
          >
            <Pencil className="h-3 w-3" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="h-7 w-7 text-destructive hover:text-destructive"
            onClick={() => onDelete(cert.id)}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </td>
    </tr>
  );
};

// ---------------------------------------------------------------------------
// Add row
// ---------------------------------------------------------------------------

const AddCertificateRow = ({
  companyId,
  onCreated,
}: {
  companyId: number;
  onCreated: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState<EditableRow>(EMPTY_ROW);
  const [create, { isPending }] = useCreate();
  const set = (f: keyof EditableRow, v: unknown) =>
    setValues((prev) => ({ ...prev, [f]: v }));

  const handleCreate = () => {
    create(
      "resale_certificates",
      {
        data: {
          company_id: companyId,
          ...values,
        },
      },
      {
        onSuccess: () => {
          setValues(EMPTY_ROW);
          setOpen(false);
          onCreated();
        },
      },
    );
  };

  if (!open) {
    return (
      <tr>
        <td colSpan={7} className="p-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setOpen(true)}
            className="gap-1"
          >
            <Plus className="h-4 w-4" /> Add Certificate
          </Button>
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-b bg-muted/20">
      <td colSpan={7} className="p-3">
        <CertificateForm values={values} set={set} />
        <div className="flex gap-2 mt-3">
          <Button
            size="sm"
            onClick={handleCreate}
            disabled={isPending || !values.certificate_number || !values.expiry_date}
          >
            <Check className="h-3 w-3 mr-1" /> Save
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setValues(EMPTY_ROW);
              setOpen(false);
            }}
          >
            <X className="h-3 w-3 mr-1" /> Cancel
          </Button>
        </div>
      </td>
    </tr>
  );
};

// ---------------------------------------------------------------------------
// Main table
// ---------------------------------------------------------------------------

export const ResaleCertificatesTable = ({
  companyId,
}: {
  companyId: number;
}) => {
  const { data, isPending } = useListContext<Certificate>();
  const refresh = useRefresh();
  const [update] = useUpdate();
  const [deleteOne] = useDelete();

  const handleSave = (id: number, updated: Partial<Certificate>, previous: Certificate) => {
    update(
      "resale_certificates",
      { id, data: updated, previousData: previous },
      { onSuccess: refresh },
    );
  };

  const handleDelete = (id: number) => {
    deleteOne(
      "resale_certificates",
      { id },
      { onSuccess: refresh },
    );
  };

  if (isPending) return null;

  return (
    <div className="w-full overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b text-xs text-muted-foreground">
            <th className="text-left p-2 font-medium">Certificate #</th>
            <th className="text-left p-2 font-medium">Type</th>
            <th className="text-left p-2 font-medium">States</th>
            <th className="text-left p-2 font-medium">Expiry</th>
            <th className="text-left p-2 font-medium">Status</th>
            <th className="text-left p-2 font-medium">File</th>
            <th className="p-2" />
          </tr>
        </thead>
        <tbody>
          {(data ?? []).map((cert) => (
            <CertificateRow
              key={cert.id}
              cert={cert}
              onDelete={handleDelete}
              onSave={handleSave}
            />
          ))}
          <AddCertificateRow companyId={companyId} onCreated={refresh} />
        </tbody>
      </table>
    </div>
  );
};
